/**
 * Personal information validation utilities
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { calculateAge } from '../utils/dateUtils';
import { ERROR_MESSAGES } from '../utils/errorMessages';
import { AGE_RESTRICTIONS } from '../constants/formOptions';

/**
 * Validates personal information form data
 * @param {Object} formData - Personal information form data
 * @returns {Object} - Validation errors object
 */
export const validatePersonalInfo = (formData) => {
  const errors = {};
  
  // Name validations
  if (!formData.firstName?.trim()) {
    errors.firstName = ERROR_MESSAGES.FIRST_NAME_REQUIRED;
  }
  
  if (!formData.lastName?.trim()) {
    errors.lastName = ERROR_MESSAGES.LAST_NAME_REQUIRED;
  }
  
  // Date of birth validation
  if (!formData.dateOfBirth) {
    errors.dateOfBirth = ERROR_MESSAGES.DOB_REQUIRED;
  } else {
    const age = calculateAge(formData.dateOfBirth);
    if (age < AGE_RESTRICTIONS.MIN_AGE) {
      errors.dateOfBirth = ERROR_MESSAGES.MIN_AGE(AGE_RESTRICTIONS.MIN_AGE);
    }
  }
  
  // Gender validation
  if (!formData.gender) {
    errors.gender = ERROR_MESSAGES.GENDER_REQUIRED;
  }
  
  // Nationality validation
  if (!formData.nationality) {
    errors.nationality = ERROR_MESSAGES.NATIONALITY_REQUIRED;
  }
  
  // Email validation
  if (!formData.email?.trim()) {
    errors.email = ERROR_MESSAGES.EMAIL_REQUIRED;
  } else if (!isValidEmail(formData.email)) {
    errors.email = ERROR_MESSAGES.INVALID_EMAIL;
  }
  
  // Phone validation
  const phoneError = validatePhoneNumber(formData.phone);
  if (phoneError) {
    errors.phone = phoneError;
  }
  
  // Address validations
  const mailingErrors = validateAddress(formData.mailingAddress, 'Mailing');
  const permanentErrors = validateAddress(formData.permanentAddress, 'Permanent');
  
  return { ...errors, ...mailingErrors, ...permanentErrors };
};

/**
 * Validates phone number
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Error message or null
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || !phone.trim()) {
    return ERROR_MESSAGES.PHONE_REQUIRED;
  }
  
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    if (!phoneNumber || !phoneNumber.isValid()) {
      return ERROR_MESSAGES.INVALID_PHONE;
    }
    return null;
  } catch (error) {
    return ERROR_MESSAGES.INVALID_PHONE;
  }
};

/**
 * Validates address fields
 * @param {Object} address - Address object
 * @param {string} type - Address type (Mailing/Permanent)
 * @returns {Object} - Validation errors
 */
export const validateAddress = (address, type) => {
  const errors = {};
  const prefix = type.toLowerCase() === 'mailing' ? 'mailingAddress' : 'permanentAddress';
  
  if (!address.address?.trim()) {
    errors[`${prefix}_address`] = ERROR_MESSAGES.ADDRESS_REQUIRED(type);
  }
  
  if (!address.city?.trim()) {
    errors[`${prefix}_city`] = ERROR_MESSAGES.CITY_REQUIRED;
  }
  
  if (!address.state?.trim()) {
    errors[`${prefix}_state`] = ERROR_MESSAGES.STATE_REQUIRED;
  }
  
  if (!address.country?.trim()) {
    errors[`${prefix}_country`] = ERROR_MESSAGES.COUNTRY_REQUIRED;
  }
  
  if (!address.zipCode?.trim()) {
    errors[`${prefix}_zipCode`] = ERROR_MESSAGES.ZIP_CODE_REQUIRED;
  }
  
  return errors;
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates name field (no numbers or special characters)
 * @param {string} name - Name to validate
 * @returns {boolean} - True if valid
 */
export const isValidName = (name) => {
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  return nameRegex.test(name);
};
